import { FormEvent, MouseEvent } from "react";
import { Box, Button, Grid } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import logo from "../../assets/images/logo.png";
import { useSearchContext } from "../../contexts/SearchContext";
import { useAppContext } from "../../contexts/AppContext";
import { queryKeys } from "../../hooks/api/queryKeys";
import FilterCategory from "./components/FilterCategory";
import FilterCategoryGroup from "./components/FilterCategoryGroup";
import FilterIndustry from "./components/FilterIndustry";
import FilterMixIdeal from "./components/FilterMixIdeal";
import FilterProductType from "./components/FilterProductType";
import SearchText from "./components/SearchText";
import { BtnSolar, Container, Form, SolarContainer } from "./styles";

const Search = () => {
  const {
    searchTerm,
    searchIndustry,
    searchMixIdeal,
    searchCategory,
    searchCategoryGroup,
    searchProductType,
    setSearchTerm,
    setSearchIndustry,
    setSearchMixIdeal,
    setSearchCategory,
    setSearchCategoryGroup,
    setSearchProductType,
  } = useSearchContext();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isCampanhaSolar, setCampanhaSolar } = useAppContext();

  const navigateHome = (autoRedirect: boolean) => {
    const newPath = location.pathname === "/pedido" ? location.pathname : "/";
    setSearchTerm("*");
    setSearchIndustry("*");
    setSearchMixIdeal("*");
    setSearchCategory("*");
    setSearchCategoryGroup("*");
    setSearchProductType("*");
    navigate(newPath, autoRedirect ? { state: { autoRedirect } } : undefined);
  };

  const clearSearchText = () => navigateHome(false);

  const enterCampain = async (event: FormEvent | MouseEvent) => {
    let entered = false;
    // Entrar na Campanha Solar
    if (window.confirm("Deseja entrar na campanha solar ?")) {
      setCampanhaSolar(true);
      // Invalida apenas as queries específicas do novo estado (S)
      // Isso evita refetch de ambas as queries (S e N)
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.order("S"),
      });
      handleSubmit(event);
      entered = true;
    }
    return entered;
  };

  const leaveCampain = async (event: FormEvent | MouseEvent) => {
    let left = false;
    // Sair da Campanha Solar
    if (window.confirm("Deseja sair da campanha solar ?")) {
      setCampanhaSolar(false);
      // Invalida apenas as queries específicas do novo estado (N)
      // Isso evita refetch de ambas as queries (S e N)
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.order("N"),
      });
      clearSearchText();
      handleSubmit(event);
      left = true;
    }
    return left;
  };

  const solarCampain = async (event: MouseEvent<HTMLButtonElement>) => {
    const changed = isCampanhaSolar
      ? await leaveCampain(event)
      : await enterCampain(event);
    navigateHome(changed);
    // A invalidação já é feita dentro de enterCampain/leaveCampain
    // Não precisa invalidar novamente aqui
  };

  const handleSubmit = (event: FormEvent | MouseEvent) => {
    event.preventDefault();
    if (
      searchTerm !== "" ||
      searchIndustry !== "0" ||
      searchMixIdeal !== "0" ||
      searchCategory !== "0" ||
      searchCategoryGroup !== "0" ||
      searchProductType !== "0"
    ) {
      navigate(
        `/produtos/buscar/${searchTerm}/${searchIndustry}/${searchProductType}/${searchCategoryGroup}/${searchCategory}/${searchMixIdeal}`
      );
    }
  };

  return (
    <Container>
      <Box mr={3} display="flex" alignItems="center">
        <img src={logo} alt="eGAM" width={250} />
      </Box>

      <Form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <FilterProductType />
          </Grid>

          <Grid size={6}>
            <FilterCategoryGroup />
          </Grid>

          <Grid size={6}>
            <FilterCategory />
          </Grid>

          <Grid size={6}>
            <FilterIndustry />
          </Grid>

          <Grid size={6}>
            <FilterMixIdeal />
          </Grid>

          <Grid size={12}>
            <Box display="flex" alignItems="center">
              <Box flexGrow="1" mr={2}>
                <SearchText />
              </Box>

              <Box mr={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  type="submit"
                >
                  Filtrar
                </Button>
              </Box>

              <Box>
                <Button
                  variant="contained"
                  color="inherit"
                  startIcon={<ClearIcon />}
                  onClick={clearSearchText}
                >
                  Limpar
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Form>

      <SolarContainer>
        {isCampanhaSolar && (
          <p>
            Você está vendo produtos somente da <strong>Campanha Solar</strong>.
          </p>
        )}
        <BtnSolar onClick={solarCampain}>
          {!isCampanhaSolar
            ? "Entrar na Campanha Solar"
            : "Sair da campanha Solar"}
        </BtnSolar>
      </SolarContainer>
    </Container>
  );
};

export default Search;
